import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Input, message, Popconfirm, Tag, Empty, Avatar, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, DownOutlined, RightOutlined, LogoutOutlined, ShareAltOutlined, UserOutlined, TeamOutlined, LockOutlined, NumberOutlined, ExportOutlined, ImportOutlined, CopyOutlined, LinkOutlined, HolderOutlined } from '@ant-design/icons';
import api, { setToken, setUser } from '../services/auth';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ dragListeners: listeners, isDragging })}
    </div>
  );
};

const SettingsPage = ({ isMobile, onLogout, user, onUserUpdate }) => {
  const [projects, setProjects] = useState([]);
  const [nodesMap, setNodesMap] = useState({});
  const [collaboratorsMap, setCollaboratorsMap] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedNodes, setExpandedNodes] = useState({});
  const [createProjectVisible, setCreateProjectVisible] = useState(false);
  const [addNodeVisible, setAddNodeVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [shareVisible, setShareVisible] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [addNodeTarget, setAddNodeTarget] = useState(null);
  const [shareProject, setShareProject] = useState(null);
  const [projectForm] = Form.useForm();
  const [nodeForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [shareForm] = Form.useForm();

  const [usernameVisible, setUsernameVisible] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [usernameForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const [exportTemplateVisible, setExportTemplateVisible] = useState(false);
  const [exportProject, setExportProject] = useState(null);
  const [templateCode, setTemplateCode] = useState('');
  const [importTemplateVisible, setImportTemplateVisible] = useState(false);
  const [importCode, setImportCode] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = async ({ active, over }, projectId, parentId) => {
    if (!over || active.id === over.id) return;
    const tree = nodesMap[projectId] || [];
    const nodes = parentId ? (tree.find(n => findNode(n, parentId))?.children || []) : tree;
    const oldIndex = nodes.findIndex(n => n.id === active.id);
    const newIndex = nodes.findIndex(n => n.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newNodes = arrayMove(nodes, oldIndex, newIndex);
    const updatedTree = parentId
      ? updateNodeInTree(tree, parentId, { children: newNodes })
      : newNodes;
    setNodesMap(prev => ({ ...prev, [projectId]: updatedTree }));
    try {
      await api.put(`/projects/${projectId}/nodes/reorder`, { nodeIds: newNodes.map(n => n.id) });
    } catch (err) {
      message.error('排序保存失败');
      loadNodes(projectId);
    }
  };

  const handleProjectDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = projects.findIndex(p => p.id === active.id);
    const newIndex = projects.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newProjects = arrayMove(projects, oldIndex, newIndex);
    setProjects(newProjects);
    try {
      await api.put('/projects/reorder', { projectIds: newProjects.map(p => p.id) });
    } catch (err) {
      message.error('排序保存失败');
      loadData();
    }
  };

  const findNode = (node, id) => {
    if (node.id === id) return node;
    if (node.children) {
      for (const child of node.children) {
        const found = findNode(child, id);
        if (found) return found;
      }
    }
    return null;
  };

  const updateNodeInTree = (tree, nodeId, updates) => {
    return tree.map(n => {
      if (n.id === nodeId) return { ...n, ...updates };
      if (n.children) return { ...n, children: updateNodeInTree(n.children, nodeId, updates) };
      return n;
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data.filter(p => !p.is_template));
    } catch (err) {
      console.error('加载数据失败:', err);
    }
  };

  const loadNodes = async (projectId) => {
    try {
      const res = await api.get(`/projects/${projectId}/nodes`);
      setNodesMap(prev => ({ ...prev, [projectId]: res.data.tree }));
    } catch (err) {
      console.error('加载节点失败:', err);
    }
  };

  const loadCollaborators = async (projectId) => {
    try {
      const res = await api.get(`/projects/${projectId}/collaborators`);
      setCollaboratorsMap(prev => ({ ...prev, [projectId]: res.data }));
    } catch (err) {
      console.error('加载协作者失败:', err);
    }
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const next = { ...prev, [projectId]: !prev[projectId] };
      if (next[projectId]) {
        if (!nodesMap[projectId]) loadNodes(projectId);
        if (!collaboratorsMap[projectId]) loadCollaborators(projectId);
      }
      return next;
    });
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const isProjectOwner = (project) => {
    return project.user_id === user?.id;
  };

  const handleCreateProject = async (values) => {
    try {
      await api.post('/projects', { name: values.name, description: '' });
      message.success('项目创建成功');
      setCreateProjectVisible(false);
      projectForm.resetFields();
      loadData();
    } catch (err) {
      message.error('创建失败: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditProject = async (values) => {
    try {
      await api.put(`/projects/${editTarget.projectId}`, { name: values.name });
      message.success('修改成功');
      setEditVisible(false);
      editForm.resetFields();
      setEditTarget(null);
      loadData();
    } catch (err) {
      message.error('修改失败');
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await api.delete(`/projects/${projectId}`);
      message.success('删除成功');
      loadData();
    } catch (err) {
      message.error(err.response?.data?.error || '删除失败');
    }
  };

  const handleAddNode = async (values) => {
    try {
      const nodeType = values.nodeType || 'button';
      let name = values.name;
      if (nodeType === 'numeric') {
        name = values.unit || '自定义数值';
      }
      await api.post(`/projects/${addNodeTarget.projectId}/nodes`, {
        name,
        parentId: addNodeTarget.parentId || null,
        nodeType,
      });
      message.success('添加成功');
      setAddNodeVisible(false);
      nodeForm.resetFields();
      setAddNodeTarget(null);
      loadNodes(addNodeTarget.projectId);
    } catch (err) {
      message.error('添加失败');
    }
  };

  const handleEditNode = async (values) => {
    try {
      await api.put(`/projects/${editTarget.projectId}/nodes/${editTarget.nodeId}`, {
        name: values.name,
      });
      message.success('修改成功');
      setEditVisible(false);
      editForm.resetFields();
      setEditTarget(null);
      loadNodes(editTarget.projectId);
    } catch (err) {
      message.error('修改失败');
    }
  };

  const handleDeleteNode = async (projectId, nodeId) => {
    try {
      await api.delete(`/projects/${projectId}/nodes/${nodeId}`);
      message.success('删除成功');
      loadNodes(projectId);
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleShare = async (values) => {
    try {
      const projectId = shareProject.id;
      await api.post(`/projects/${projectId}/share`, {
        collaboratorUsername: values.username,
      });
      message.success('分享成功');
      setShareVisible(false);
      shareForm.resetFields();
      setShareProject(null);
      loadCollaborators(projectId);
    } catch (err) {
      message.error(err.response?.data?.error || '分享失败');
    }
  };

  const handleRemoveCollaborator = async (projectId, shareId) => {
    try {
      await api.delete(`/projects/${projectId}/collaborators/${shareId}`);
      message.success('已移除协作者');
      loadCollaborators(projectId);
    } catch (err) {
      message.error(err.response?.data?.error || '移除失败');
    }
  };

  const handleUpdateUsername = async (values) => {
    try {
      const res = await api.put('/account/username', { username: values.username });
      message.success('用户名修改成功');
      setToken(res.data.token);
      setUser(res.data.user);
      if (onUserUpdate) onUserUpdate(res.data.user);
      setUsernameVisible(false);
      usernameForm.resetFields();
    } catch (err) {
      message.error(err.response?.data?.error || '修改失败');
    }
  };

  const handleUpdatePassword = async (values) => {
    try {
      await api.put('/account/password', {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      message.success('密码修改成功');
      setPasswordVisible(false);
      passwordForm.resetFields();
    } catch (err) {
      message.error(err.response?.data?.error || '修改失败');
    }
  };

  const openEditProject = (project) => {
    setEditTarget({ type: 'project', projectId: project.id, name: project.name });
    editForm.setFieldsValue({ name: project.name });
    setEditVisible(true);
  };

  const openEditNode = (projectId, node) => {
    setEditTarget({ type: 'node', projectId, nodeId: node.id, name: node.name });
    editForm.setFieldsValue({ name: node.name });
    setEditVisible(true);
  };

  const openAddNode = (projectId, parentId) => {
    setAddNodeTarget({ projectId, parentId });
    nodeForm.resetFields();
    nodeForm.setFieldsValue({ nodeType: 'button' });
    setAddNodeVisible(true);
  };

  const handleExportTemplate = async (project) => {
    try {
      const res = await api.post(`/projects/${project.id}/export-template`);
      const baseUrl = window.location.origin;
      const code = res.data.templateCode;
      setTemplateCode(`${baseUrl}/import/${code}`);
      setExportProject(project);
      setExportTemplateVisible(true);
    } catch (err) {
      message.error('导出失败: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCopyTemplateLink = () => {
    navigator.clipboard.writeText(templateCode).then(() => {
      message.success('链接已复制到剪贴板');
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = templateCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      message.success('链接已复制到剪贴板');
    });
  };

  const handleImportTemplate = async () => {
    if (!importCode.trim()) {
      message.warning('请输入模板链接');
      return;
    }
    setImportLoading(true);
    try {
      let code = importCode.trim();
      const match = code.match(/\/import\/(.+)$/);
      if (match) code = match[1];
      const res = await api.post('/projects/import-template', { templateCode: code });
      message.success(`项目「${res.data.name}」导入成功`);
      setImportTemplateVisible(false);
      setImportCode('');
      loadData();
    } catch (err) {
      message.error('导入失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setImportLoading(false);
    }
  };

  const renderNodeList = (nodes, projectId, parentId, depth, isOwner) => {
    if (!isOwner) {
      return nodes.map(node => renderNodeContent(node, projectId, depth, isOwner, null));
    }
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => handleDragEnd(event, projectId, parentId)}>
        <SortableContext items={nodes.map(n => n.id)} strategy={verticalListSortingStrategy}>
          {nodes.map(node => (
            <SortableItem key={node.id} id={node.id}>
              {({ dragListeners }) => renderNodeContent(node, projectId, depth, isOwner, dragListeners)}
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>
    );
  };

  const renderNodeContent = (node, projectId, depth, isOwner, dragListeners) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.id];
    const paddingLeft = depth * 20;
    const isNumeric = node.node_type === 'numeric';

    return (
      <div key={node.id}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          paddingLeft,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          gap: 4,
        }}>
          {isOwner && (
            <span {...dragListeners} style={{ cursor: 'grab', color: '#c7c7cc', flexShrink: 0, touchAction: 'none' }}>
              <HolderOutlined />
            </span>
          )}
          <span
            onClick={() => hasChildren && toggleNode(node.id)}
            style={{ cursor: hasChildren ? 'pointer' : 'default', width: 20, textAlign: 'center', flexShrink: 0, color: '#86868b' }}
          >
            {hasChildren ? (isExpanded ? <DownOutlined /> : <RightOutlined />) : <span style={{ opacity: 0.3 }}>—</span>}
          </span>
          <span style={{ flex: 1, fontSize: isMobile ? 13 : 14, fontWeight: 500 }}>
            {node.name}
            {isNumeric && <Tag color="blue" style={{ marginLeft: 6, marginBottom: 2, borderRadius: 8, fontSize: 11 }}>自定义数值</Tag>}
          </span>
          {isOwner && (
            <>
              <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => openAddNode(projectId, node.id)} title="添加子节点" />
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditNode(projectId, node)} title="编辑" />
              <Popconfirm title="确定删除该节点及其子节点？" onConfirm={() => handleDeleteNode(projectId, node.id)}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} title="删除" />
              </Popconfirm>
            </>
          )}
        </div>
        {isExpanded && hasChildren && renderNodeList(node.children, projectId, node.id, depth + 1, isOwner)}
      </div>
    );
  };

  const renderCollaborators = (project) => {
    const collaborators = collaboratorsMap[project.id] || [];
    const isOwner = isProjectOwner(project);

    return (
      <div style={{
        borderTop: '1px solid rgba(0,0,0,0.06)',
        padding: '10px 12px',
        background: 'rgba(0,0,0,0.02)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: collaborators.length > 0 ? 8 : 0,
          gap: 6,
        }}>
          <TeamOutlined style={{ color: '#007AFF' }} />
          <span style={{ fontWeight: 600, fontSize: isMobile ? 12 : 13, color: '#1d1d1f' }}>
            协作者 ({collaborators.length})
          </span>
          {isOwner && (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => { setShareProject(project); shareForm.resetFields(); setShareVisible(true); }}
              style={{ padding: 0, marginLeft: 'auto', fontSize: isMobile ? 11 : 12 }}
            >
              添加
            </Button>
          )}
        </div>
        {collaborators.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {collaborators.map(c => (
              <Tag
                key={c.share_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  margin: 0,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: isMobile ? 11 : 12,
                }}
                closable={isOwner}
                onClose={(e) => {
                  e.preventDefault();
                  handleRemoveCollaborator(project.id, c.share_id);
                }}
              >
                <Avatar size={14} icon={<UserOutlined />} style={{ background: '#007AFF' }} />
                {c.username}
              </Tag>
            ))}
          </div>
        ) : (
          <div style={{ color: '#86868b', fontSize: isMobile ? 11 : 12 }}>
            {isOwner ? '暂无协作者，点击添加' : '暂无协作者'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 800 }}>
      <Card
        title="账号"
        size={isMobile ? 'small' : 'medium'}
        style={{ marginBottom: 16 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 2 }}>{user?.username}</div>
              <div style={{ color: '#86868b', fontSize: 13 }}>用户名</div>
            </div>
            <Button type="text" icon={<EditOutlined />} onClick={() => { usernameForm.setFieldsValue({ username: user?.username }); setUsernameVisible(true); }}>
              修改
            </Button>
          </div>

          <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 2 }}>
                <LockOutlined style={{ marginRight: 6, color: '#86868b' }} />
                密码
              </div>
              <div style={{ color: '#86868b', fontSize: 13 }}>••••••</div>
            </div>
            <Button type="text" icon={<EditOutlined />} onClick={() => { passwordForm.resetFields(); setPasswordVisible(true); }}>
              修改
            </Button>
          </div>

          <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <Popconfirm title="确定退出登录？" onConfirm={onLogout}>
              <Button danger icon={<LogoutOutlined />} style={{ borderRadius: 12 }}>退出登录</Button>
            </Popconfirm>
          </div>
        </div>
      </Card>

      <Card
        title="项目管理"
        size={isMobile ? 'small' : 'medium'}
        style={{ marginBottom: 16 }}
        extra={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon={<ImportOutlined />} onClick={() => { setImportCode(''); setImportTemplateVisible(true); }}>
              导入模板
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { projectForm.resetFields(); setCreateProjectVisible(true); }}>
              新建项目
            </Button>
          </div>
        }
      >
        {projects.length > 0 ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleProjectDragEnd}>
            <SortableContext items={projects.map(p => p.id)} strategy={verticalListSortingStrategy}>
              {projects.map(project => {
                const isExpanded = expandedProjects[project.id];
                const tree = nodesMap[project.id] || [];
                const isOwner = isProjectOwner(project);
                return (
                  <SortableItem key={project.id} id={project.id}>
                    {({ dragListeners }) => (
                      <div style={{
                        border: '1px solid rgba(0,0,0,0.06)',
                        borderRadius: 12,
                        marginBottom: 8,
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 12px',
                          background: 'rgba(0,0,0,0.02)',
                          gap: 4,
                        }}>
                          {isOwner && (
                            <span {...dragListeners} style={{ cursor: 'grab', color: '#c7c7cc', flexShrink: 0, touchAction: 'none' }}>
                              <HolderOutlined />
                            </span>
                          )}
                          <span
                            onClick={() => toggleProject(project.id)}
                            style={{ cursor: 'pointer', width: 20, textAlign: 'center', flexShrink: 0, color: '#86868b' }}
                          >
                            {isExpanded ? <DownOutlined /> : <RightOutlined />}
                          </span>
                          <span style={{ flex: 1, fontWeight: 600, fontSize: isMobile ? 14 : 15 }}>
                            {project.name}
                            {!isOwner && <Tag color="green" style={{ marginLeft: 6, marginBottom: 2 }}>共享</Tag>}
                          </span>
                          {isOwner && (
                            <>
                              <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => openAddNode(project.id, null)} title="添加子节点" />
                              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditProject(project)} title="编辑" />
                              <Button type="text" size="small" icon={<ShareAltOutlined />} onClick={() => { setShareProject(project); shareForm.resetFields(); setShareVisible(true); }} title="分享" />
                              <Button type="text" size="small" icon={<ExportOutlined />} onClick={() => handleExportTemplate(project)} title="导出模板" />
                              <Popconfirm title="确定删除该项目？" onConfirm={() => handleDeleteProject(project.id)}>
                                <Button type="text" size="small" danger icon={<DeleteOutlined />} title="删除" />
                              </Popconfirm>
                            </>
                          )}
                        </div>
                        {isExpanded && (
                          <div>
                            {tree.length > 0 ? renderNodeList(tree, project.id, null, 1, isOwner) : (
                              <div style={{ padding: '16px 12px 16px 32px', color: '#86868b', fontSize: 13 }}>
                                暂无子节点{isOwner ? '，点击 + 添加' : ''}
                              </div>
                            )}
                            {renderCollaborators(project)}
                          </div>
                        )}
                      </div>
                    )}
                  </SortableItem>
                );
              })}
            </SortableContext>
          </DndContext>
        ) : (
          <Empty description="暂无项目，点击右上角新建" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      <Modal
        title="修改用户名"
        open={usernameVisible}
        onCancel={() => setUsernameVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 400}
      >
        <Form form={usernameForm} onFinish={handleUpdateUsername} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="username" label="新用户名" rules={[{ required: true, message: '请输入用户名' }, { min: 2, message: '至少2个字符' }]}>
            <Input placeholder="输入新用户名" autoFocus />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block style={{ borderRadius: 12 }}>确认修改</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="修改密码"
        open={passwordVisible}
        onCancel={() => setPasswordVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 400}
      >
        <Form form={passwordForm} onFinish={handleUpdatePassword} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="oldPassword" label="旧密码" rules={[{ required: true, message: '请输入旧密码' }]}>
            <Input.Password placeholder="输入旧密码" autoFocus />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '至少6位' }]}>
            <Input.Password placeholder="输入新密码" />
          </Form.Item>
          <Form.Item name="confirmPassword" label="确认新密码" dependencies={['newPassword']} rules={[
            { required: true, message: '请确认密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                return Promise.reject(new Error('两次密码不一致'));
              },
            }),
          ]}>
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block style={{ borderRadius: 12 }}>确认修改</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="新建项目"
        open={createProjectVisible}
        onCancel={() => setCreateProjectVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 400}
      >
        <Form form={projectForm} onFinish={handleCreateProject} layout="vertical">
          <Form.Item name="name" label="项目名称" rules={[{ required: true, message: '请输入项目名称' }]}>
            <Input placeholder="输入项目名称" autoFocus />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>创建</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="添加子节点"
        open={addNodeVisible}
        onCancel={() => { setAddNodeVisible(false); setAddNodeTarget(null); }}
        footer={null}
        width={isMobile ? '95%' : 400}
      >
        <Form form={nodeForm} onFinish={handleAddNode} layout="vertical">
          <Form.Item name="nodeType" label="节点类型" rules={[{ required: true }]}>
            <Select options={[
              { value: 'button', label: <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: '#34C759', marginRight: 8 }} />普通按钮</span> },
              { value: 'numeric', label: <span><NumberOutlined style={{ color: '#007AFF', marginRight: 8 }} />数值</span> },
            ]} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.nodeType !== cur.nodeType}>
            {({ getFieldValue }) => {
              const nodeType = getFieldValue('nodeType');
              if (nodeType === 'numeric') {
                return (
                  <Form.Item name="unit" label="单位（选填）">
                    <Input placeholder="例如：ml、次、分钟、℃" autoFocus />
                  </Form.Item>
                );
              }
              return (
                <Form.Item name="name" label="节点名称" rules={[{ required: true, message: '请输入节点名称' }]}>
                  <Input placeholder="输入节点名称" autoFocus />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>添加</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="编辑"
        open={editVisible}
        onCancel={() => { setEditVisible(false); setEditTarget(null); }}
        footer={null}
        width={isMobile ? '95%' : 400}
      >
        <Form form={editForm} onFinish={editTarget?.type === 'project' ? handleEditProject : handleEditNode} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="输入名称" autoFocus />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>保存</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`分享项目：${shareProject?.name || ''}`}
        open={shareVisible}
        onCancel={() => { setShareVisible(false); setShareProject(null); }}
        footer={null}
        width={isMobile ? '95%' : 400}
      >
        <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(0,122,255,0.06)', borderRadius: 12, fontSize: 13, color: '#007AFF', fontWeight: 500 }}>
          分享后，对方可以查看和记录该项目，记录历史将实时共享同步。只有项目所有者可以管理协作者。
        </div>
        <Form form={shareForm} onFinish={handleShare} layout="vertical">
          <Form.Item name="username" label="协作用户名" rules={[{ required: true, message: '请输入协作用户名' }]}>
            <Input placeholder="输入对方的用户名" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>分享</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`导出项目模板：${exportProject?.name || ''}`}
        open={exportTemplateVisible}
        onCancel={() => { setExportTemplateVisible(false); setExportProject(null); setTemplateCode(''); }}
        footer={null}
        width={isMobile ? '95%' : 480}
      >
        <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(0,122,255,0.06)', borderRadius: 12, fontSize: 13, color: '#007AFF', fontWeight: 500 }}>
          <LinkOutlined style={{ marginRight: 6 }} />
          将项目结构（含节点和统计配置）导出为模板链接，他人通过链接可直接导入使用。
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#1d1d1f' }}>模板链接</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              value={templateCode}
              readOnly
              style={{ flex: 1, fontSize: 12, borderRadius: 10 }}
            />
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={handleCopyTemplateLink}
              style={{ borderRadius: 10, fontWeight: 500 }}
            >
              复制
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        title="导入项目模板"
        open={importTemplateVisible}
        onCancel={() => { setImportTemplateVisible(false); setImportCode(''); }}
        footer={null}
        width={isMobile ? '95%' : 480}
      >
        <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(52,199,89,0.06)', borderRadius: 12, fontSize: 13, color: '#248a3d', fontWeight: 500 }}>
          <ImportOutlined style={{ marginRight: 6 }} />
          粘贴他人分享的项目模板链接，即可导入完整的项目结构（含节点和统计配置）。
        </div>
        <div style={{ marginBottom: 16 }}>
          <Input.TextArea
            value={importCode}
            onChange={e => setImportCode(e.target.value)}
            placeholder="粘贴模板链接"
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ borderRadius: 10 }}
          />
        </div>
        <Button
          type="primary"
          icon={<ImportOutlined />}
          block
          loading={importLoading}
          onClick={handleImportTemplate}
          style={{ height: 44, fontSize: 15, fontWeight: 600, borderRadius: 12 }}
        >
          导入项目
        </Button>
      </Modal>
    </div>
  );
};

export default SettingsPage;
